import React from 'react';
import PropTypes from 'prop-types';

import {dispatch, connect} from 'utils/state_manager';

import {Box, Button, Chip, CircularProgress, Divider, Stack, Typography, screenSizeBigger, StickyTooltip} from 'views/components';
import {Check} from 'views/icons';
import {CommandResponseView, ConnectionBadge, HoverToolBox, Translate, translate} from 'views/module';
import CommandTextViewer from '../../../../module/task/components/CommandTextViewer';

import {TASK_TYPE_CONFIG} from '../../utils/taskTypeConfig';

import backend from 'utils/backend';
import moment from 'moment';

const TaskItem = (props) => {
  const {
    task,
    handleSetStatusToComplete,
    onClickEditTask,
    removeTask,
    requester,
    statusProgressWrap: progressWrap,
    playbooks,
    skills,
  } = props;
  const isStatusComplete = task.status === 'COMPLETE';
  const largerScreen = screenSizeBigger('sm');

  const isDueDateADate = moment(task.dueDate, moment.ISO_8601, true).isValid();
  const dueDateMoment = isDueDateADate ? moment(task.dueDate) : null;
  const isOverdue = isDueDateADate ?
    dueDateMoment.isBefore(moment()) :
    false;
  const overdueDayCount = isDueDateADate ?
    moment().startOf('day').diff(dueDateMoment.clone().startOf('day'), 'days') :
    0;
  const maxDisplayedAvatarIcons = 5;
  const connections = task.connectionInfo || [];
  const ownerRns = Array.isArray(task.ownerRns) ? task.ownerRns :
    Array.isArray(task.ownerRn) ? task.ownerRn :
      task.ownerRn ? [task.ownerRn] : [];
  const owners = (task.ownerInfo || []).length > 0 ?
    task.ownerInfo :
    ownerRns
        .filter(Boolean)
        .map((ownerRn) => ({userRn: ownerRn, name: ownerRn}));
  const visibleConnections = connections.slice(0, maxDisplayedAvatarIcons);
  const hiddenConnections = connections.slice(maxDisplayedAvatarIcons);
  const hasDueDateDisplay = isDueDateADate || Boolean(task.dueDate && !isDueDateADate);
  const descriptionText = typeof task.description === 'string' ?
    task.description :
    task.description?.blob || translate('tasks.MyDay.TaskItem.Description.Empty');
  const playbook = playbooks.find((entry) => entry.playbookRn === task.playbookRn) || null;
  const playbookEngagementPlan = task.playbookEngagementPlan || null;
  const engagementRuleRn = task.engagementRuleRn || null;
  const engagementResponseType = playbookEngagementPlan?.responseType || null;
  const skillRn = task.skillRn || null;

  const skill = React.useMemo(() => {
    if (!skillRn) {
      return null;
    }

    return skills.find((entry) => entry.skillRn === skillRn) || null;
  }, [skillRn, skills]);
  const [commandExecutionResponse, setCommandExecutionResponse] = React.useState(null);
  const [lastExecutedCommand, setLastExecutedCommand] = React.useState(null);
  const [commandExecutionContext, setCommandExecutionContext] = React.useState(null);
  const [isExecutingCommand, setIsExecutingCommand] = React.useState(false);
  const communicationTarget = commandExecutionContext?.communicationTarget || null;

  const [toolbarVisible, setToolbarVisible] = React.useState(false);
  const [anchorEl, setAnchorEl] = React.useState(null);
  const rightEdgeAnchorRef = React.useRef(null);

  const handleMouseEnter = () => {
    if (largerScreen) {
      setToolbarVisible(true);
      setAnchorEl(rightEdgeAnchorRef.current);
    }
  };

  const handleMouseLeave = () => {
    if (largerScreen) {
      setToolbarVisible(false);
      setAnchorEl(null);
    }
  };

  React.useEffect(() => {
    setToolbarVisible(false);
    setAnchorEl(null);
  }, [largerScreen]);

  React.useEffect(() => {
    setCommandExecutionResponse(null);
    setLastExecutedCommand(null);
    setCommandExecutionContext(null);
  }, [engagementRuleRn, skillRn, task.taskRn]);

  const getSkillResources = (runtimeConnections) => {
    return [
      task.clientRn,
      ...(task.connections || []),
      ...runtimeConnections.map((connection) => connection.connectionRn),
    ]
        .filter(Boolean)
        .reduce((resources, resourceRn) => {
          const resourceType = /^RN:CLIENT-V1-[^:]+$/.test(resourceRn) ?
            'CLIENT' :
            /^RN:CAREGIVER-V1-[^:]+$/.test(resourceRn) ?
              'CAREGIVER' :
              null;

          if (!resourceType || resources.some((resource) => resource.rn === resourceRn)) {
            return resources;
          }

          return [
            ...resources,
            {
              type: resourceType,
              rn: resourceRn,
            },
          ];
        }, []);
  };

  const hydrateTaskForRun = async () => {
    const connectionInfo = await Promise.all((task.connections || []).map(async (connectionRn) => {
      try {
        if (/^RN:CLIENT-V1-[^:]+$/.test(connectionRn)) {
          const clientResponse = await backend.client.getClient(connectionRn, false);
          const clientNameParts = (clientResponse?.clientDetail.clientName || '').trim().split(/\s+/).filter(Boolean);
          const [firstName = '', ...lastNameParts] = clientNameParts;
          const relationships = Object.values(clientResponse?.clientDetail.relationships || {})
              .filter((relationship) => {
                return relationship?.relationshipRn &&
                  [
                    relationship?.name,
                    relationship?.contacts?.find((contact) => contact?.type === 'Email')?.detail,
                    relationship?.contacts?.find((contact) => contact?.type === 'Phone')?.detail,
                  ].some(Boolean);
              })
              .sort((firstRelationship, secondRelationship) => {
                return firstRelationship?.priority - secondRelationship?.priority;
              });

          const defaultRelationship = relationships.find((relationship) => {
            return Number(relationship?.priority) === 1;
          }) || relationships[0] || null;

          return {
            connectionRn,
            firstName,
            lastName: lastNameParts.join(' '),

            communicationTarget: {
              defaultRelationship,
              relationships,
              getRelationshipEmail: (relationship) => relationship?.contacts?.find((contact) => {
                return contact?.type === 'Email';
              })?.detail || '',
              getRelationshipSms: (relationship) => relationship?.contacts?.find((contact) => {
                return contact?.type === 'Phone';
              })?.detail || '',

              sendEmail: async (emailData, targetRelationshipRn, onSuccess=()=>undefined, onError=()=>undefined) => {
                const selectedRelationship = relationships.find((relationship) => {
                  return relationship.relationshipRn === targetRelationshipRn;
                }) || defaultRelationship;
                const relationshipRn = selectedRelationship?.relationshipRn;

                if (!relationshipRn) {
                  onError();
                  return null;
                }

                return progressWrap(async () => {
                  await backend.notification.sendEmail(relationshipRn, emailData);
                  onSuccess();
                }, {debugString: 'CLIENT_MESSAGE_SEND_EMAIL'});
              },

              sendSms: async (smsData, requestUser, targetRelationshipRn, onSuccess=()=>undefined, onError=()=>undefined) => {
                const selectedRelationship = relationships.find((relationship) => {
                  return relationship.relationshipRn === targetRelationshipRn;
                }) || defaultRelationship;
                const relationshipRn = selectedRelationship?.relationshipRn;

                if (!relationshipRn) {
                  onError();
                  return null;
                }

                return progressWrap(async () => {
                  await backend.notification.sendSms(
                      relationshipRn,
                      smsData.message,
                      requestUser?.email,
                  );
                  onSuccess();
                }, {debugString: 'CLIENT_MESSAGE_SEND_SMS'});
              },

              updateCareplan: async (careplan, progressOptions={}) => {
                return progressWrap(async () => {
                  await backend.client.updateCareplan(connectionRn, careplan);
                }, {
                  ...progressOptions,
                  debugString: 'CLIENT_CAREPLAN_UPDATE',
                });
              },
            },
          };
        }

        if (/^RN:CAREGIVER-V1-[^:]+$/.test(connectionRn)) {
          const caregiverResponse = await backend.caregiver.getCaregiver(connectionRn, false);
          const caregiverNameParts = (caregiverResponse?.caregiverDetail?.caregiverName || '').trim().split(/\s+/).filter(Boolean);
          const [firstName = '', ...lastNameParts] = caregiverNameParts;
          const relationship = {
            email: caregiverResponse?.caregiverBio?.questions?.email?.questionData?.value || '',
            name: caregiverResponse?.caregiverDetail?.caregiverName || '',
            phoneNumber: caregiverResponse?.caregiverBio?.questions?.phoneNumber?.questionData?.value || '',
            relationshipRn: connectionRn,
          };
          const relationships = [relationship].filter((entry) => {
            return [entry.name, entry.email, entry.phoneNumber].some(Boolean);
          });

          return {
            connectionRn,
            firstName,
            lastName: lastNameParts.join(' '),

            communicationTarget: {
              defaultRelationship: relationships[0] || null,
              relationships,
              getRelationshipEmail: (relationshipOption) => relationshipOption?.email,
              getRelationshipSms: (relationshipOption) => relationshipOption?.phoneNumber,

              sendEmail: async (emailData, _relationshipRn, onSuccess=()=>undefined, _onError=()=>undefined) => {
                return progressWrap(async () => {
                  await backend.notification.sendEmail(connectionRn, emailData);
                  onSuccess();
                }, {debugString: 'CAREGIVER_MESSAGE_SEND_EMAIL'});
              },
            },
          };
        }
      } catch {
        return null;
      }

      return null;
    }));

    const ownerInfo = ownerRns.includes(requester?.userRn) ? [{
      ownerRn: requester.userRn,
      firstName: requester.firstName,
      lastName: requester.lastName,
    }] : [];

    return {
      ...task,
      connectionInfo: connectionInfo.filter(Boolean),
      ownerInfo,
    };
  };

  const executeSkill = async (selectedSkill, promptText=' ') => {
    let response = null;

    await progressWrap(async () => {
      try {
        setIsExecutingCommand(true);
        const taskRunData = await hydrateTaskForRun();
        const taskRunConnections = taskRunData.connectionInfo || [];
        const requestBody = {
          ressources: [
            ...getSkillResources(taskRunConnections),
            {type: 'USER', rn: requester.userRn},
          ],
          text: promptText || ' ',
        };

        setLastExecutedCommand({
          type: 'skill',
          promptText,
          skill: selectedSkill,
        });

        setCommandExecutionContext({
          communicationTarget: taskRunConnections[0]?.communicationTarget || null,
        });

        response = await backend.skills.executeSkill(selectedSkill.skillRn, requestBody);
        setCommandExecutionResponse(response?.result || null);
      } finally {
        setIsExecutingCommand(false);
      }
    }, {
      showProgress: false,
    });

    return response;
  };

  const handleExecuteSkill = async () => {
    if (skill?.skillRn && !isExecutingCommand) {
      await executeSkill(skill);
    }
  };

  const handleExecuteEngagement = async () => {
    let response = null;

    await progressWrap(async () => {
      try {
        setIsExecutingCommand(true);
        const taskRunData = await hydrateTaskForRun();
        const taskRunConnections = taskRunData.connectionInfo || [];

        setLastExecutedCommand({
          type: 'engagement',
        });

        setCommandExecutionContext({
          communicationTarget: taskRunConnections[0]?.communicationTarget || null,
        });

        response = await backend.playbook.generateEngagementResponse(playbookEngagementPlan);
        response = response?.result || response || {};
        setCommandExecutionResponse({
          outputType: response.outputType || response.responseType || engagementResponseType,
          subject: response.subject || '',
          content: response.content || response.message || '',
          message: response.message || response.content || '',
        });
      } finally {
        setIsExecutingCommand(false);
      }
    }, {
      showProgress: false,
    });

    return response;
  };

  const handleRegenerate = () => {
    if (!lastExecutedCommand) {
      return null;
    }

    if (lastExecutedCommand.type === 'skill') {
      return executeSkill(lastExecutedCommand.skill, lastExecutedCommand.promptText);
    }

    if (lastExecutedCommand.type === 'engagement') {
      return handleExecuteEngagement();
    }

    return null;
  };

  const handleDiscard = () => {
    setCommandExecutionResponse(null);
    setCommandExecutionContext(null);
  };

  const getConnectionDisplayName = (connection) => [connection.firstName, connection.lastName].filter(Boolean).join(' ');
  const canRunSkillTask = Boolean(skill?.skillRn);
  const canRunEngagementTask = Boolean(!isStatusComplete && engagementRuleRn && engagementResponseType && playbookEngagementPlan);
  const canRunCommand = canRunSkillTask || canRunEngagementTask;
  const handleRunCommand = canRunSkillTask ? handleExecuteSkill : handleExecuteEngagement;
  const handleApproveAndSendSuccess = () => {
    handleSetStatusToComplete(task.taskRn, isStatusComplete);
  };

  return (
    <Stack
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      sx={{
        position: 'relative',
        opacity: isStatusComplete ? 0.45 : 1,
      }}
    >
      <Stack
        spacing={1.25}
        sx={{
          py: 2,
          pr: 3,
        }}
      >
        <Stack
          direction="row"
          spacing={2}
          alignItems="center"
        >
          <Box
            onClick={() => handleSetStatusToComplete(task.taskRn, isStatusComplete)}
            sx={{
              'width': 24,
              'height': 24,
              'borderRadius': '50%',
              'border': '1.5px solid',
              'borderColor': isStatusComplete ? 'success.main' : 'divider',
              'backgroundColor': isStatusComplete ? 'success.main' : 'transparent',
              'display': 'flex',
              'alignItems': 'center',
              'justifyContent': 'center',
              'cursor': 'pointer',
              '& .todo-check': {
                transition: 'color 120ms ease',
                color: isStatusComplete ? 'common.white' : 'transparent',
                fontSize: 10,
              },
              '&:hover': {
                borderColor: isStatusComplete ? 'success.main' : 'success.main',
              },
              '&:hover .todo-check': {
                color: isStatusComplete ? 'common.white' : 'success.main',
              },
            }}
          >
            <Check className="todo-check" />
          </Box>
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            sx={{
              flex: 1,
              minWidth: 0,
            }}
          >
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              sx={{
                flexWrap: 'wrap',
                flex: 1,
                minWidth: 0,
              }}
            >
              {
                task?.type && (() => {
                  const type = task.type;
                  const typeConfig = TASK_TYPE_CONFIG[type] ?? TASK_TYPE_CONFIG.UNKNOWN;

                  return (
                    <Chip
                      key={type}
                      variant="outlined"
                      label={typeConfig.labelKey ? translate(typeConfig.labelKey) : type}
                      size="small"
                      sx={{
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        letterSpacing: '0.04em',
                        ...typeConfig.chipSx,
                        borderWidth: 1,
                      }}
                    />
                  );
                })()
              }
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 600,
                  textDecoration: isStatusComplete ? 'line-through' : 'none',
                  minWidth: 0,
                }}
              >
                <CommandTextViewer
                  value={task.title}
                  owners={owners}
                  connections={connections}
                  type={task.type}
                />
                {
                  canRunCommand &&
                  <Box
                    component='span'
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 1,
                      ml: 1,
                    }}
                  >
                    <Box
                      component='span'
                      sx={{
                        color: 'text.secondary',
                        fontWeight: 400,
                      }}
                    >
                      -
                    </Box>
                    {
                      isExecutingCommand && (
                        <Box
                          component='span'
                          sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: '#4B4843',
                            minWidth: '20px',
                            marginLeft: '12px',
                          }}
                        >
                          <CircularProgress color='inherit' size={20} />
                        </Box>
                      )
                    }
                    {
                      !isExecutingCommand && (
                        <Button
                          variant='outlined'
                          size='small'
                          onClick={handleRunCommand}
                          sx={{
                            'minWidth': '30px',
                            'px': '12px',
                            'py': '8px',
                            'borderRadius': '7px',
                            'borderColor': '#D8D4CE',
                            'background': '#FCFCFC',
                            'color': '#4B4843',
                            'fontSize': '11px',
                            'fontWeight': 500,
                            'lineHeight': '100%',
                            'textTransform': 'none',
                          }}
                        >
                          Run
                        </Button>
                      )
                    }
                  </Box>
                }
              </Typography>
            </Stack>
          </Stack>
        </Stack>
        <Stack
          spacing={1}
          sx={{
            pl: '40px',
          }}
        >
          <Typography
            variant='body2'
            color='text.secondary'
            sx={{
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
            }}
          >
            <CommandTextViewer
              value={descriptionText}
              owners={owners}
              connections={connections}
              type={task.type}
            />
          </Typography>
          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            sx={{
              flexWrap: 'wrap',
            }}
          >
            {
              isDueDateADate &&
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 600,
                  color: isOverdue && !isStatusComplete ? 'error.main' : 'text.secondary',
                }}
              >
                {
                  overdueDayCount === 1 && (
                    <Translate
                      expression="tasks.MyDay.TaskItem.Due.YesterdayLabel"
                      substitute={{time: dueDateMoment.format('h:mmA')}}
                    />
                  )
                }

                {
                  overdueDayCount > 1 &&
                    <Translate
                      expression="tasks.MyDay.TaskItem.Due.DaysAgoLabel"
                      substitute={{
                        days: overdueDayCount,
                        time: dueDateMoment.format('h:mmA'),
                      }}
                    />
                }
                {
                  overdueDayCount < 1 &&
                    <Translate
                      expression="tasks.MyDay.TaskItem.Due.Label"
                      substitute={{time: dueDateMoment.format('h:mmA')}}
                    />
                }
              </Typography>
            }
            {
              (task.dueDate && !isDueDateADate) &&
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 600,
                  color: 'text.secondary',
                }}
              >
                {task.dueDate}
              </Typography>
            }
            <Box
              sx={{
                display: hasDueDateDisplay && connections.length > 0 ? 'block' : 'none',
                height: 4,
                width: 4,
                borderRadius: '50%',
                backgroundColor: 'text.secondary',
              }}
            />
            {
              connections.length > 0 &&
                <Stack
                  direction="row"
                  spacing={-.5}
                  sx={{
                    alignItems: 'center',
                  }}
                >
                  {
                    visibleConnections.map((connection, index) => (
                      <ConnectionBadge
                        key={connection.connectionRn}
                        connection={connection}
                        sx={{
                          width: '28px',
                          height: '28px',
                          position: 'relative',
                          zIndex: index + 1,
                          fontSize: '13px',
                          outline: '2px solid',
                          outlineColor: 'background.paper',
                        }}
                      />
                    ))
                  }
                  {
                    hiddenConnections.length > 0 &&
                      <StickyTooltip
                        title={hiddenConnections.map((connection) => getConnectionDisplayName(connection)).join(', ')}
                      >
                        <Box
                          sx={{
                            width: '28px',
                            height: '28px',
                            position: 'relative',
                            zIndex: visibleConnections.length + 1,
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: '#E2DEDA',
                            color: 'text.secondary',
                            fontSize: '11px',
                            fontWeight: 600,
                            outline: '2px solid',
                            outlineColor: 'background.paper',
                          }}
                        >
                          +{hiddenConnections.length}
                        </Box>
                      </StickyTooltip>
                  }
                </Stack>
            }
            {
              Boolean(playbook) &&
                <>
                  <Box
                    sx={{
                      height: 4,
                      width: 4,
                      borderRadius: '50%',
                      backgroundColor: 'text.secondary',
                    }}
                  />
                  <Typography
                    variant='caption'
                    component='button'
                    onClick={() => window.open(`/playbooks?selectedPlaybookRn=${encodeURIComponent(playbook.playbookRn)}`)}
                    sx={{
                      'background': 'none',
                      'border': 'none',
                      'color': '#DA7955',
                      'cursor': 'pointer',
                      'fontSize': '12px',
                      'p': 0,
                      '&:hover': {
                        textDecoration: 'underline',
                      },
                    }}
                  >
                    /{playbook.title}
                  </Typography>
                </>
            }
          </Stack>
          {
            commandExecutionResponse &&
            <CommandResponseView
              communicationTarget={communicationTarget}
              requester={requester}
              response={commandExecutionResponse}
              onApproveAndSendSuccess={handleApproveAndSendSuccess}
              onDiscard={handleDiscard}
              isRegenerating={isExecutingCommand}
              onRegenerate={handleRegenerate}
            />
          }
        </Stack>
      </Stack>
      <Box
        ref={rightEdgeAnchorRef}
        sx={{
          position: 'absolute',
          right: -100,
          top: 20,
          width: 0,
          height: 0,
        }}
      />
      <HoverToolBox
        toolBar={toolbarVisible}
        anchorEl={anchorEl}
        onClickEdit={() => onClickEditTask(task)}
        onClickDelete={() => removeTask(task)}
        hideTool={{
          emoji: true,
          copy: true,
          edit: false,
          delete: false,
          reply: true,
        }}
      />
      <Divider />
    </Stack>
  );
};

TaskItem.propTypes = {
  task: PropTypes.object.isRequired,
  handleSetStatusToComplete: PropTypes.func.isRequired,
  onClickEditTask: PropTypes.func,
  removeTask: PropTypes.func,
  requester: PropTypes.object.isRequired,
  statusProgressWrap: PropTypes.func.isRequired,
  playbooks: PropTypes.array.isRequired,
  skills: PropTypes.array.isRequired,
};

const mapStateToProps = (state) => ({
  playbooks: state.playbooks.allPlaybooks,
  requester: state.user,
  skills: state.skills.allSkills,
});

export default connect(mapStateToProps, dispatch)(React.memo(TaskItem));
